import {
  isLeadEventType,
  leadAlertTarget,
  leadAlertText,
} from "../../features/leads/core/utils/notifyText";

/**
 * Wording and click targets for every notification event type, shared by the
 * toast layer (CommsNotifyProvider) and the bell (NotificationBell).
 */

/**
 * Ticket event types that earn their own toast, and what it says.
 *
 * `TICKET_CREATED` fans out to the department's agents so a pool arrival lands
 * immediately instead of waiting for the next resync diff; `SLA_BREACH` carries
 * `payload.clock` telling us which of the two deadlines was missed, and latches
 * once per clock — so this fires once per ticket, not once a minute.
 *
 * @type {Object<string, { title: (clock: string|null) => string, fallback: string, toastPrefix: string }>}
 */
export const TICKET_ALERTS = {
  TICKET_ASSIGNED: {
    title: () => "Ticket assigned to you",
    fallback: "Open the support console to respond",
    toastPrefix: "ticket-assigned",
  },
  TICKET_CREATED: {
    title: () => "New ticket in the pool",
    fallback: "Open the support console to pick it up",
    toastPrefix: "ticket-created",
  },
  SLA_BREACH: {
    title: (clock) =>
      clock === "next" ? "Reply SLA breached" : "First-reply SLA breached",
    fallback: "This ticket is past its deadline",
    toastPrefix: "ticket-sla",
  },
};

/** Chat-side event types: a room id opens the conversation. */
const CHAT_EVENTS = {
  AI_INTERVENED: { title: "The assistant stepped in on a chat", fallback: "Open the conversation" },
  FLAG: { title: "A message was flagged", fallback: "Open the conversation" },
  CRISIS: { title: "Crisis alert on a chat", fallback: "Open the conversation now" },
};

const str = (value) => (typeof value === "string" && value.trim() ? value.trim() : null);

/**
 * Normalize a feed row (`{id, event_type, room_id, payload, read_at,
 * created_at}`) or a live publication (`{type:"notification", event_id,
 * event_type, room_id, payload, created_at?}`) into one bell item.
 *
 * @param {Object} raw
 * @param {string} [fallbackCreatedAt] - Used when a live event has no time.
 * @returns {{ id: string, eventType: string, roomId: string|null,
 *   payload: Object, readAt: string|null, createdAt: string|null }|null}
 */
export function normalizeNotification(raw, fallbackCreatedAt = null) {
  if (!raw || typeof raw !== "object") return null;
  const id = raw.id ?? raw.event_id;
  const eventType = str(raw.event_type);
  if (id === null || id === undefined || !eventType) return null;
  const payload = raw.payload && typeof raw.payload === "object" ? raw.payload : {};
  const roomId = raw.room_id ?? payload.room_id ?? null;
  return {
    id: String(id),
    eventType,
    roomId: roomId !== null && roomId !== undefined ? String(roomId) : null,
    payload,
    readAt: raw.read_at || null,
    createdAt: raw.created_at || fallbackCreatedAt,
  };
}

/**
 * Title, body and target for one normalized notification.
 * @param {{ eventType: string, roomId: string|null, payload: Object }} item
 * @returns {{ title: string, body: string, target: string|null }}
 */
export function describeNotification(item) {
  const { eventType, roomId } = item;
  const payload = item.payload || {};
  if (isLeadEventType(eventType)) {
    return { ...leadAlertText(eventType, payload), target: leadAlertTarget(eventType, payload) };
  }
  const subject = str(payload.subject) || str(payload.title);

  const ticket = TICKET_ALERTS[eventType];
  if (ticket) {
    return {
      title: ticket.title(payload.clock ?? null),
      body: subject || ticket.fallback,
      target: roomId ? `/support/tickets/${encodeURIComponent(roomId)}` : "/support/console",
    };
  }
  const chatTarget = roomId ? `/messages/${encodeURIComponent(roomId)}` : null;
  if (eventType === "MENTION") {
    const sender = str(payload.sender_name);
    return {
      title: sender ? `${sender} mentioned you` : "You were mentioned",
      body: subject || "Open the conversation",
      target: chatTarget || "/messages",
    };
  }
  const chat = CHAT_EVENTS[eventType];
  if (chat) {
    return { title: chat.title, body: subject || chat.fallback, target: chatTarget };
  }
  if (eventType === "BROADCAST_RESULT") {
    return { title: "Your broadcast has finished", body: subject || "See how it went", target: "/broadcasts" };
  }
  return { title: "New notification", body: subject || "", target: null };
}
